import { useRef, useCallback, useState } from 'react';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

export function useWebRTC() {
  const peerConnections = useRef(new Map()); // viewerId -> RTCPeerConnection
  const localStream = useRef(null);
  const [connectionState, setConnectionState] = useState('disconnected');

  // Get user media (camera + mic)
  const getLocalStream = useCallback(async (constraints = { video: true, audio: true }) => {
    try {
      // Try preferred constraints first
      const preferredConstraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      try {
        localStream.current = await navigator.mediaDevices.getUserMedia(preferredConstraints);
      } catch {
        // Fallback to basic constraints
        localStream.current = await navigator.mediaDevices.getUserMedia(constraints);
      }
      
      return localStream.current;
    } catch (error) {
      console.error('Failed to get local stream:', error);
      throw error;
    }
  }, []);

  // Stop local stream
  const stopLocalStream = useCallback(() => {
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => track.stop());
      localStream.current = null;
    }
  }, []);

  // Create peer connection for a viewer (broadcaster side)
  const createPeerConnectionForViewer = useCallback((viewerId, onIceCandidate) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        onIceCandidate(viewerId, event.candidate);
      }
    };

    pc.onconnectionstatechange = () => {
      console.log(`Connection state for ${viewerId}:`, pc.connectionState);
      setConnectionState(pc.connectionState);
    };

    // Add local tracks to the connection
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => {
        pc.addTrack(track, localStream.current);
      });
    }

    peerConnections.current.set(viewerId, pc);
    return pc;
  }, []);

  // Create offer for a viewer (broadcaster side)
  const createOffer = useCallback(async (viewerId, onIceCandidate) => {
    let pc = peerConnections.current.get(viewerId);
    
    if (!pc) {
      pc = createPeerConnectionForViewer(viewerId, onIceCandidate);
    }

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      return offer;
    } catch (error) {
      console.error('Failed to create offer:', error);
      throw error;
    }
  }, [createPeerConnectionForViewer]);

  // Handle answer from viewer (broadcaster side)
  const handleAnswer = useCallback(async (viewerId, sdp) => {
    const pc = peerConnections.current.get(viewerId);
    if (pc) {
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      } catch (error) {
        console.error('Failed to set remote description:', error);
      }
    }
  }, []);

  // Handle ICE candidate (broadcaster side)
  const handleIceCandidate = useCallback(async (viewerId, candidate) => {
    const pc = peerConnections.current.get(viewerId);
    if (pc && candidate) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (error) {
        console.error('Failed to add ICE candidate:', error);
      }
    }
  }, []);

  // Create peer connection for viewer (viewer side)
  const createViewerConnection = useCallback((onIceCandidate, onTrack) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        onIceCandidate(event.candidate);
      }
    };

    pc.ontrack = (event) => {
      console.log('Received track:', event.track.kind);
      onTrack(event.streams[0]);
    };

    pc.onconnectionstatechange = () => {
      console.log('Viewer connection state:', pc.connectionState);
      setConnectionState(pc.connectionState);
    };

    peerConnections.current.set('viewer', pc);
    return pc;
  }, []);

  // Handle offer from broadcaster (viewer side)
  const handleOffer = useCallback(async (sdp, onIceCandidate, onTrack) => {
    let pc = peerConnections.current.get('viewer');
    
    if (!pc) {
      pc = createViewerConnection(onIceCandidate, onTrack);
    }

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      return answer;
    } catch (error) {
      console.error('Failed to handle offer:', error);
      throw error;
    }
  }, [createViewerConnection]);

  // Handle ICE candidate (viewer side)
  const handleViewerIceCandidate = useCallback(async (candidate) => {
    const pc = peerConnections.current.get('viewer');
    if (pc && candidate) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (error) {
        console.error('Failed to add ICE candidate:', error);
      }
    }
  }, []);

  // Close all connections
  const closeAllConnections = useCallback(() => {
    peerConnections.current.forEach((pc) => {
      pc.close();
    });
    peerConnections.current.clear();
    stopLocalStream();
    setConnectionState('disconnected');
  }, [stopLocalStream]);

  // Close specific viewer connection
  const closeViewerConnection = useCallback((viewerId) => {
    const pc = peerConnections.current.get(viewerId);
    if (pc) {
      pc.close();
      peerConnections.current.delete(viewerId);
    }
  }, []);

  // Switch camera (front/back)
  const switchCamera = useCallback(async () => {
    if (!localStream.current) return;

    const videoTrack = localStream.current.getVideoTracks()[0];
    if (!videoTrack) return;

    const currentFacingMode = videoTrack.getSettings().facingMode;
    const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacingMode },
        audio: true
      });

      const newVideoTrack = newStream.getVideoTracks()[0];
      
      // Replace track in all peer connections
      peerConnections.current.forEach((pc) => {
        const sender = pc.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          sender.replaceTrack(newVideoTrack);
        }
      });

      // Stop old track and update local stream
      videoTrack.stop();
      localStream.current.removeTrack(videoTrack);
      localStream.current.addTrack(newVideoTrack);

      return localStream.current;
    } catch (error) {
      console.error('Failed to switch camera:', error);
    }
  }, []);

  // Toggle mute
  const toggleMute = useCallback((muted) => {
    if (localStream.current) {
      localStream.current.getAudioTracks().forEach(track => {
        track.enabled = !muted;
      });
    }
  }, []);

  // Toggle video
  const toggleVideo = useCallback((disabled) => {
    if (localStream.current) {
      localStream.current.getVideoTracks().forEach(track => {
        track.enabled = !disabled;
      });
    }
  }, []);

  return {
    localStream: localStream.current,
    connectionState,
    getLocalStream,
    stopLocalStream,
    createOffer,
    handleAnswer,
    handleIceCandidate,
    handleOffer,
    handleViewerIceCandidate,
    closeAllConnections,
    closeViewerConnection,
    switchCamera,
    toggleMute,
    toggleVideo
  };
}
